import md5 from 'md5';

import OrganizationType from '@/common/models/memberships/OrganizationType.enum';

import Exchange, { IExchangeAPI } from './Exchange';
import Organization, { IOrganizationAPI } from './Organization';

interface IMembershipAPI {
  curator: IOrganizationAPI[];
  exch: IExchangeAPI[];
  dsp: IOrganizationAPI[];
  ssp: IOrganizationAPI[];
}

export default class Membership {
  curator: Organization[];
  exchanges: Exchange[];
  dsp: Organization[];
  ssp: Organization[];
  dspParent: Organization[];
  sspParent: Organization[];

  constructor(
    curator: Organization[],
    exchanges: Exchange[],
    dsp: Organization[],
    ssp: Organization[],
    dspParent: Organization[],
    sspParent: Organization[],
  ) {
    this.curator = curator;
    this.exchanges = exchanges;
    this.dsp = dsp;
    this.ssp = ssp;
    this.dspParent = dspParent;
    this.sspParent = sspParent;
  }

  static fromApi(apiObject: IMembershipAPI): Membership {
    return new Membership(
      apiObject.curator.map((x: IOrganizationAPI) => Organization.fromApi(x)),
      apiObject.exch.map((x: IExchangeAPI) => Exchange.fromApi(x)),
      apiObject.dsp
        .filter((x) => x.type === OrganizationType.DEMAND)
        .map((x: IOrganizationAPI) => Organization.fromApi(x)),
      apiObject.ssp
        .filter((x) => x.type === OrganizationType.SUPPLY)
        .map((x: IOrganizationAPI) => Organization.fromApi(x)),
      apiObject.dsp
        .filter((x) => x.type === OrganizationType.DEMAND_PARENT)
        .map((x: IOrganizationAPI) => Organization.fromApi(x)),
      apiObject.ssp
        .filter((x) => x.type === OrganizationType.SUPPLY_PARENT)
        .map((x: IOrganizationAPI) => Organization.fromApi(x)),
    );
  }

  findOrg(organizationID: number): Organization | null {
    const org =
      this.dsp.find((x) => x.id === organizationID) ||
      this.ssp.find((x) => x.id === organizationID) ||
      this.dspParent?.find((x) => x.id === organizationID) ||
      this.sspParent?.find((x) => x.id === organizationID) ||
      this.curator.find((x) => x.id === organizationID) ||
      null;
    const childOrgs = [
      ...this.dsp.filter((x) => x.parentOrganizationID === organizationID),
      ...this.ssp.filter((x) => x.parentOrganizationID === organizationID),
    ];

    org.childOrganizations = childOrgs;

    return org;
  }

  findOrgMd5(organizationID: string): Organization | null {
    return (
      this.dsp.find((x) => md5(x.id) === organizationID) ||
      this.ssp.find((x) => md5(x.id) === organizationID) ||
      this.dspParent?.find((x) => md5(x.id) === organizationID) ||
      this.sspParent?.find((x) => md5(x.id) === organizationID) ||
      this.curator.find((x) => md5(x.id) === organizationID) ||
      null
    );
  }

  findExch(exchangeID: number): Exchange | null {
    return this.exchanges.find((x) => x.id === exchangeID) || null;
  }

  toApi(): IMembershipAPI {
    return {
      curator: this.ssp.map((x) => x.toApi()),
      exch: this.exchanges.map((x) => x.toApi()),
      dsp: [...this.dsp.map((x) => x.toApi()), ...this.dspParent.map((x) => x.toApi())],
      ssp: [...this.ssp.map((x) => x.toApi()), ...this.sspParent.map((x) => x.toApi())],
    };
  }
}
